<template>
  <div class="reward-catalog-page">
    <!-- Reward Form Modal -->
    <div v-if="showItemModal" class="modal-overlay" @mousedown.self="closeItemModal">
      <div class="modal-panel" role="dialog" aria-modal="true">
        <div class="modal-header">
          <h2 class="modal-title">{{ itemModalMode === 'create' ? $t('admin.rewards.modal_create_title') : $t('admin.rewards.modal_edit_title') }}</h2>
          <button class="modal-close" title="Close" @click="closeItemModal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <form class="modal-body" @submit.prevent="submitItemForm">
          <div class="form-section">
            <h3 class="section-title">{{ $t('admin.rewards.section_basic') }}</h3>
            <div class="form-row">
              <label class="form-label">
                {{ $t('admin.rewards.field_name') }} *
                <input v-model="itemForm.name" type="text" class="form-input" :placeholder="$t('admin.rewards.field_name_placeholder')" required />
              </label>
            </div>
            <div class="form-row">
              <label class="form-label">
                {{ $t('admin.rewards.field_description') }}
                <textarea v-model="itemForm.description" class="form-textarea" rows="2" :placeholder="$t('admin.rewards.field_description_placeholder')"></textarea>
              </label>
            </div>
            <div class="form-row two-col">
              <label class="form-label">
                {{ $t('admin.rewards.field_category') }} *
                <select v-model="itemForm.category_id" class="form-select" required>
                  <option value="">{{ $t('admin.rewards.field_category_placeholder') }}</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </label>
              <label class="form-label">
                {{ $t('admin.rewards.field_reward_type') }} *
                <select v-model="itemForm.reward_type" class="form-select" required @change="onTypeChange">
                  <option value="">{{ $t('admin.rewards.field_reward_type_placeholder') }}</option>
                  <option value="bonus_credits">{{ $t('admin.rewards.type_bonus_credits') }}</option>
                  <option value="free_spins">{{ $t('admin.rewards.type_free_spins') }}</option>
                  <option value="cashback_boost">{{ $t('admin.rewards.type_cashback_boost') }}</option>
                  <option value="physical">{{ $t('admin.rewards.type_physical') }}</option>
                  <option value="tournament_entry">{{ $t('admin.rewards.type_tournament_entry') }}</option>
                </select>
              </label>
            </div>
            <div class="form-row two-col">
              <label class="form-label">
                {{ $t('admin.rewards.field_point_cost') }} *
                <input v-model.number="itemForm.point_cost" type="number" class="form-input" min="1" step="1" placeholder="e.g. 500" required />
              </label>
              <label class="form-label">
                {{ $t('admin.rewards.field_stock') }}
                <input v-model="itemForm.stock_quantity" type="number" class="form-input" min="0" step="1" :placeholder="$t('admin.rewards.field_stock_placeholder')" />
                <span class="form-hint">{{ $t('admin.rewards.field_stock_hint') }}</span>
              </label>
            </div>
            <div class="form-row two-col">
              <label class="form-label">
                {{ $t('admin.rewards.field_vip_tier') }}
                <select v-model="itemForm.vip_tier_requirement" class="form-select">
                  <option :value="null">{{ $t('admin.rewards.vip_no_requirement') }}</option>
                  <option :value="0">{{ $t('admin.rewards.vip_bronze') }}</option>
                  <option :value="1">{{ $t('admin.rewards.vip_silver') }}</option>
                  <option :value="2">{{ $t('admin.rewards.vip_gold') }}</option>
                  <option :value="3">{{ $t('admin.rewards.vip_platinum') }}</option>
                  <option :value="4">{{ $t('admin.rewards.vip_diamond') }}</option>
                </select>
              </label>
              <label class="form-label">
                {{ $t('admin.rewards.field_image_url') }}
                <input v-model="itemForm.image_path" type="text" class="form-input" placeholder="https://cdn.example.com/image.png" />
              </label>
            </div>
            <div class="form-row">
              <label class="form-label toggle-label">
                <span>{{ $t('admin.rewards.field_active') }}</span>
                <div class="toggle-wrap">
                  <input v-model="itemForm.is_active" type="checkbox" class="toggle-input" />
                  <div class="toggle-track" @click="itemForm.is_active = !itemForm.is_active">
                    <div class="toggle-thumb"></div>
                  </div>
                  <span class="toggle-state">{{ itemForm.is_active ? $t('admin.rewards.yes') : $t('admin.rewards.no') }}</span>
                </div>
              </label>
            </div>
          </div>

          <!-- Type-specific config fields -->
          <div v-if="itemForm.reward_type" class="form-section">
            <h3 class="section-title">{{ $t('admin.rewards.section_config') }}</h3>

            <!-- bonus_credits -->
            <template v-if="itemForm.reward_type === 'bonus_credits'">
              <div class="form-row">
                <label class="form-label">
                  {{ $t('admin.rewards.config_bonus_amount') }} *
                  <input v-model.number="itemForm.config.amount" type="number" class="form-input" min="1" step="1" placeholder="e.g. 5000 = $50.00" required />
                  <span class="form-hint">{{ $t('admin.rewards.config_bonus_amount_hint') }}</span>
                </label>
              </div>
            </template>

            <!-- free_spins -->
            <template v-else-if="itemForm.reward_type === 'free_spins'">
              <div class="form-row two-col">
                <label class="form-label">
                  {{ $t('admin.rewards.config_spins') }} *
                  <input v-model.number="itemForm.config.spins" type="number" class="form-input" min="1" step="1" placeholder="e.g. 25" required />
                </label>
                <label class="form-label">
                  {{ $t('admin.rewards.config_spin_value') }} *
                  <input v-model="itemForm.config.spin_value" type="text" class="form-input" placeholder="e.g. 0.10" required />
                </label>
              </div>
              <div class="form-row">
                <label class="form-label">
                  {{ $t('admin.rewards.config_game_slug') }}
                  <input v-model="itemForm.config.game_slug" type="text" class="form-input" placeholder="e.g. fortune-reels" />
                  <span class="form-hint">{{ $t('admin.rewards.config_game_slug_hint') }}</span>
                </label>
              </div>
            </template>

            <!-- cashback_boost -->
            <template v-else-if="itemForm.reward_type === 'cashback_boost'">
              <div class="form-row two-col">
                <label class="form-label">
                  {{ $t('admin.rewards.config_cashback_pct') }} *
                  <input v-model.number="itemForm.config.percentage" type="number" class="form-input" min="1" max="100" step="1" placeholder="e.g. 5" required />
                </label>
                <label class="form-label">
                  {{ $t('admin.rewards.config_duration_hours') }} *
                  <input v-model.number="itemForm.config.duration_hours" type="number" class="form-input" min="1" step="1" placeholder="e.g. 24" required />
                </label>
              </div>
            </template>

            <!-- physical / tournament_entry -->
            <template v-else>
              <p class="config-note">
                <template v-if="itemForm.reward_type === 'physical'">{{ $t('admin.rewards.config_physical_note') }}</template>
                <template v-else>{{ $t('admin.rewards.config_tournament_note') }}</template>
              </p>
            </template>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-ghost" @click="closeItemModal">{{ $t('admin.rewards.btn_cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="itemSaving">
              <span v-if="itemSaving" class="spinner-xs"></span>
              {{ itemModalMode === 'create' ? $t('admin.rewards.btn_create') : $t('admin.rewards.btn_save_changes') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Category Form Modal -->
    <div v-if="showCatModal" class="modal-overlay" @mousedown.self="closeCatModal">
      <div class="modal-panel modal-panel-sm" role="dialog" aria-modal="true">
        <div class="modal-header">
          <h2 class="modal-title">{{ catModalMode === 'create' ? $t('admin.rewards.cat_modal_create_title') : $t('admin.rewards.cat_modal_edit_title') }}</h2>
          <button class="modal-close" title="Close" @click="closeCatModal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <form class="modal-body" @submit.prevent="submitCatForm">
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.rewards.cat_field_name') }} *
              <input v-model="catForm.name" type="text" class="form-input" :placeholder="$t('admin.rewards.cat_field_name_placeholder')" required />
            </label>
          </div>
          <div class="form-row">
            <label class="form-label">
              {{ $t('admin.rewards.cat_field_sort_order') }}
              <input v-model.number="catForm.sort_order" type="number" class="form-input" min="0" step="1" placeholder="0" />
            </label>
          </div>
          <div class="form-row">
            <label class="form-label toggle-label">
              <span>{{ $t('admin.rewards.field_active') }}</span>
              <div class="toggle-wrap">
                <input v-model="catForm.is_active" type="checkbox" class="toggle-input" />
                <div class="toggle-track" @click="catForm.is_active = !catForm.is_active">
                  <div class="toggle-thumb"></div>
                </div>
                <span class="toggle-state">{{ catForm.is_active ? $t('admin.rewards.yes') : $t('admin.rewards.no') }}</span>
              </div>
            </label>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-ghost" @click="closeCatModal">{{ $t('admin.rewards.btn_cancel') }}</button>
            <button type="submit" class="btn btn-primary" :disabled="catSaving">
              <span v-if="catSaving" class="spinner-xs"></span>
              {{ catModalMode === 'create' ? $t('admin.rewards.btn_create_category') : $t('admin.rewards.btn_save_changes') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.rewards.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.rewards.subtitle') }}</p>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="openCreateCatModal">{{ $t('admin.rewards.btn_add_category') }}</button>
        <button class="btn btn-primary" @click="openCreateItemModal">{{ $t('admin.rewards.btn_add_reward') }}</button>
      </div>
    </div>

    <!-- Category Management Section -->
    <div class="section-block">
      <div class="section-header">
        <h2 class="section-title-lg">{{ $t('admin.rewards.section_categories') }}</h2>
      </div>
      <div v-if="catsLoading" class="section-loading">
        <span class="spinner"></span> {{ $t('admin.rewards.loading_categories') }}
      </div>
      <div v-else class="categories-grid">
        <div
          v-for="cat in categories"
          :key="cat.id"
          class="cat-chip"
          :class="{ 'cat-chip-inactive': !cat.is_active }"
        >
          <span class="cat-name">{{ cat.name }}</span>
          <span class="cat-count">{{ $t('admin.rewards.items_count', { n: cat.items_count ?? 0 }) }}</span>
          <button class="cat-edit-btn" title="Edit" @click="openEditCatModal(cat)">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        </div>
        <div v-if="categories.length === 0" class="cat-empty">{{ $t('admin.rewards.categories_empty') }}</div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <select v-model="filters.category_id" class="filter-select" @change="fetchRewards(1)">
        <option value="">{{ $t('admin.rewards.filter_all_categories') }}</option>
        <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
      </select>

      <div class="search-wrap">
        <input
          v-model="filters.search"
          type="text"
          class="filter-input search-input"
          :placeholder="$t('admin.rewards.filter_search_placeholder')"
          @keyup.enter="fetchRewards(1)"
        />
        <button class="btn-search" @click="fetchRewards(1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Reward Items Table -->
    <div class="table-container">
      <div v-if="loading" class="table-loading">
        <span class="spinner"></span>
        {{ $t('admin.rewards.loading') }}
      </div>

      <template v-else>
        <table>
          <thead>
            <tr>
              <th>{{ $t('admin.rewards.col_name') }}</th>
              <th>{{ $t('admin.rewards.col_category') }}</th>
              <th>{{ $t('admin.rewards.col_point_cost') }}</th>
              <th>{{ $t('admin.rewards.col_stock') }}</th>
              <th>{{ $t('admin.rewards.col_vip_tier') }}</th>
              <th>{{ $t('admin.rewards.col_type') }}</th>
              <th>{{ $t('admin.rewards.col_active') }}</th>
              <th>{{ $t('admin.rewards.col_actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="rewards.length > 0">
              <tr v-for="item in rewards" :key="item.id">
                <td>
                  <strong class="item-name">{{ item.name }}</strong>
                  <div v-if="item.description" class="item-desc">{{ item.description }}</div>
                </td>
                <td>
                  <span class="cat-badge">{{ item.category?.name ?? '—' }}</span>
                </td>
                <td class="td-mono">{{ item.point_cost.toLocaleString() }} {{ $t('admin.rewards.pts') }}</td>
                <td class="td-mono">
                  <span v-if="item.stock_quantity === null" class="text-muted">{{ $t('admin.rewards.unlimited') }}</span>
                  <span v-else :class="item.stock_quantity === 0 ? 'text-danger' : ''">{{ item.stock_quantity }}</span>
                </td>
                <td>
                  <span v-if="item.vip_tier_requirement !== null" class="badge badge-vip">
                    {{ vipTierLabel(item.vip_tier_requirement) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <span class="badge badge-type" :class="'badge-type-' + item.reward_type">
                    {{ typeLabel(item.reward_type) }}
                  </span>
                </td>
                <td>
                  <span class="badge" :class="item.is_active ? 'badge-active' : 'badge-inactive'">
                    {{ item.is_active ? $t('admin.rewards.yes') : $t('admin.rewards.no') }}
                  </span>
                </td>
                <td>
                  <div class="action-buttons">
                    <button class="btn-action" title="Edit" @click="openEditItemModal(item)">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      class="btn-action btn-action-danger"
                      title="Deactivate"
                      :disabled="deletingId === item.id"
                      @click="deactivateReward(item)"
                    >
                      <span v-if="deletingId === item.id" class="spinner-xs spinner-xs-danger"></span>
                      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </template>
            <tr v-else>
              <td colspan="8" class="empty-row">{{ $t('admin.rewards.empty') }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="meta.last_page > 1" class="pagination">
          <button
            class="pagination-btn"
            :disabled="meta.current_page <= 1"
            @click="fetchRewards(meta.current_page - 1)"
          >&larr;</button>
          <button
            v-for="page in visiblePages"
            :key="page"
            class="pagination-btn"
            :class="{ active: page === meta.current_page }"
            @click="fetchRewards(page)"
          >{{ page }}</button>
          <span class="pagination-info">
            {{ $t('admin.rewards.showing', { start: (meta.current_page - 1) * meta.per_page + 1, end: Math.min(meta.current_page * meta.per_page, meta.total), total: meta.total }) }}
          </span>
          <button
            class="pagination-btn"
            :disabled="meta.current_page >= meta.last_page"
            @click="fetchRewards(meta.current_page + 1)"
          >&rarr;</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

// ---------------------------------------------------------------
// Types
// ---------------------------------------------------------------

interface RewardCategory {
  id: number
  name: string
  slug: string
  sort_order: number
  is_active: boolean
  items_count?: number
}

interface RewardConfig {
  amount?: number
  spins?: number
  spin_value?: string
  game_slug?: string
  percentage?: number
  duration_hours?: number
}

interface RewardItem {
  id: number
  category_id: number
  category?: RewardCategory
  name: string
  description: string | null
  image_path: string | null
  point_cost: number
  stock_quantity: number | null
  vip_tier_requirement: number | null
  reward_type: string
  reward_config: RewardConfig | null
  is_active: boolean
}

interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

// ---------------------------------------------------------------
// State
// ---------------------------------------------------------------

const { t } = useI18n()
const toast = useToastStore()

const rewards = ref<RewardItem[]>([])
const categories = ref<RewardCategory[]>([])
const loading = ref(false)
const catsLoading = ref(false)
const meta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 15, total: 0 })
const deletingId = ref<number | null>(null)

const filters = ref({
  category_id: '' as number | '',
  search: '',
})

// ---------------------------------------------------------------
// Reward item modal
// ---------------------------------------------------------------

const showItemModal = ref(false)
const itemModalMode = ref<'create' | 'edit'>('create')
const editingItemId = ref<number | null>(null)
const itemSaving = ref(false)

function emptyItemForm() {
  return {
    name: '',
    description: '',
    category_id: '' as number | '',
    reward_type: '',
    point_cost: null as number | null,
    stock_quantity: '' as number | '',
    vip_tier_requirement: null as number | null,
    image_path: '',
    is_active: true,
    config: {} as RewardConfig,
  }
}

const itemForm = ref(emptyItemForm())

function onTypeChange() {
  itemForm.value.config = {}
}

function openCreateItemModal() {
  itemForm.value = emptyItemForm()
  itemModalMode.value = 'create'
  editingItemId.value = null
  showItemModal.value = true
}

function openEditItemModal(item: RewardItem) {
  itemForm.value = {
    name: item.name,
    description: item.description ?? '',
    category_id: item.category_id,
    reward_type: item.reward_type,
    point_cost: item.point_cost,
    stock_quantity: item.stock_quantity ?? '',
    vip_tier_requirement: item.vip_tier_requirement,
    image_path: item.image_path ?? '',
    is_active: item.is_active,
    config: item.reward_config ? { ...item.reward_config } : {},
  }
  itemModalMode.value = 'edit'
  editingItemId.value = item.id
  showItemModal.value = true
}

function closeItemModal() {
  showItemModal.value = false
}

async function submitItemForm() {
  itemSaving.value = true
  try {
    const payload = {
      name: itemForm.value.name,
      description: itemForm.value.description || null,
      category_id: itemForm.value.category_id,
      reward_type: itemForm.value.reward_type,
      point_cost: itemForm.value.point_cost,
      stock_quantity: itemForm.value.stock_quantity === '' ? null : itemForm.value.stock_quantity,
      vip_tier_requirement: itemForm.value.vip_tier_requirement,
      image_path: itemForm.value.image_path || null,
      is_active: itemForm.value.is_active,
      reward_config: buildRewardConfig(),
    }

    if (itemModalMode.value === 'create') {
      await apiClient.post('/admin/api/rewards', payload)
      toast.success(t('admin.rewards.toast_created'))
    } else {
      await apiClient.put(`/admin/api/rewards/${editingItemId.value}`, payload)
      toast.success(t('admin.rewards.toast_updated'))
    }

    closeItemModal()
    await fetchRewards(meta.value.current_page)
  } catch {
    toast.error(t('admin.rewards.error_save'))
  } finally {
    itemSaving.value = false
  }
}

function buildRewardConfig(): RewardConfig | null {
  const type = itemForm.value.reward_type
  const c = itemForm.value.config
  if (type === 'bonus_credits') return { amount: c.amount }
  if (type === 'free_spins') return { spins: c.spins, spin_value: c.spin_value, game_slug: c.game_slug || undefined }
  if (type === 'cashback_boost') return { percentage: c.percentage, duration_hours: c.duration_hours }
  return null
}

async function deactivateReward(item: RewardItem) {
  if (!confirm(t('admin.rewards.deactivate_confirm', { name: item.name }))) return
  if (deletingId.value !== null) return
  deletingId.value = item.id
  try {
    await apiClient.delete(`/admin/api/rewards/${item.id}`)
    toast.success(t('admin.rewards.toast_deactivated', { name: item.name }))
    await fetchRewards(meta.value.current_page)
  } catch {
    toast.error(t('admin.rewards.error_deactivate'))
  } finally {
    deletingId.value = null
  }
}

// ---------------------------------------------------------------
// Category modal
// ---------------------------------------------------------------

const showCatModal = ref(false)
const catModalMode = ref<'create' | 'edit'>('create')
const editingCatId = ref<number | null>(null)
const catSaving = ref(false)

function emptyCatForm() {
  return { name: '', sort_order: 0, is_active: true }
}

const catForm = ref(emptyCatForm())

function openCreateCatModal() {
  catForm.value = emptyCatForm()
  catModalMode.value = 'create'
  editingCatId.value = null
  showCatModal.value = true
}

function openEditCatModal(cat: RewardCategory) {
  catForm.value = { name: cat.name, sort_order: cat.sort_order, is_active: cat.is_active }
  catModalMode.value = 'edit'
  editingCatId.value = cat.id
  showCatModal.value = true
}

function closeCatModal() {
  showCatModal.value = false
}

async function submitCatForm() {
  catSaving.value = true
  try {
    if (catModalMode.value === 'create') {
      await apiClient.post('/admin/api/reward-categories', catForm.value)
      toast.success(t('admin.rewards.cat_toast_created'))
    } else {
      await apiClient.put(`/admin/api/reward-categories/${editingCatId.value}`, catForm.value)
      toast.success(t('admin.rewards.cat_toast_updated'))
    }
    closeCatModal()
    await fetchCategories()
  } catch {
    toast.error(t('admin.rewards.cat_error_save'))
  } finally {
    catSaving.value = false
  }
}

// ---------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------

async function fetchRewards(page: number): Promise<void> {
  loading.value = true
  try {
    const params: Record<string, string | number> = { page, per_page: 15 }
    if (filters.value.category_id !== '') params.category_id = filters.value.category_id as number
    if (filters.value.search) params.search = filters.value.search
    const response = await apiClient.get('/admin/api/rewards', { params })
    rewards.value = response.data.data as RewardItem[]
    meta.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
    }
  } catch {
    toast.error(t('admin.rewards.error_load'))
  } finally {
    loading.value = false
  }
}

async function fetchCategories(): Promise<void> {
  catsLoading.value = true
  try {
    const response = await apiClient.get('/admin/api/reward-categories')
    categories.value = response.data as RewardCategory[]
  } catch {
    toast.error(t('admin.rewards.cat_error_load'))
  } finally {
    catsLoading.value = false
  }
}

// ---------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------

const visiblePages = computed(() => {
  const pages: number[] = []
  const total = meta.value.last_page
  const current = meta.value.current_page
  const start = Math.max(1, current - 2)
  const end = Math.min(total, current + 2)
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
})

function typeLabel(type: string): string {
  const labels: Record<string, string> = {
    bonus_credits: t('admin.rewards.type_bonus_credits'),
    free_spins: t('admin.rewards.type_free_spins'),
    cashback_boost: t('admin.rewards.type_cashback_boost'),
    physical: t('admin.rewards.type_physical'),
    tournament_entry: t('admin.rewards.type_tournament_entry'),
  }
  return labels[type] ?? type
}

function vipTierLabel(tier: number): string {
  const labels: Record<number, string> = {
    0: t('admin.rewards.vip_bronze'),
    1: t('admin.rewards.vip_silver'),
    2: t('admin.rewards.vip_gold'),
    3: t('admin.rewards.vip_platinum'),
    4: t('admin.rewards.vip_diamond'),
  }
  return labels[tier] ?? t('admin.rewards.vip_tier_n', { n: tier })
}

onMounted(() => {
  fetchCategories()
  fetchRewards(1)
})
</script>

<style scoped>
.reward-catalog-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-md, 8px);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition: all 150ms ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: var(--accent-primary, #00e701);
  color: #000;
  letter-spacing: 0.02em;
}

.btn-primary:hover:not(:disabled) {
  background: #00cc01;
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-secondary {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-secondary:hover:not(:disabled) {
  border-color: rgba(255, 255, 255, 0.25);
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary, #b1bad3);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.btn-ghost:hover {
  border-color: rgba(255, 255, 255, 0.25);
  color: var(--text-primary, #fff);
}

/* Section Block (Categories) */
.section-block {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.section-title-lg {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.section-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
}

.categories-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cat-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-full, 9999px);
  padding: 6px 12px;
  font-size: 13px;
  color: var(--text-primary, #fff);
}

.cat-chip-inactive {
  opacity: 0.5;
}

.cat-name {
  font-weight: 500;
}

.cat-count {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.cat-edit-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-tertiary, #7f8fa4);
  display: flex;
  align-items: center;
  padding: 0;
  transition: color 150ms ease;
}

.cat-edit-btn:hover {
  color: var(--accent-primary, #00e701);
}

.cat-empty {
  color: var(--text-tertiary, #7f8fa4);
  font-size: 13px;
  font-style: italic;
}

/* Filters */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select,
.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
}

.filter-select option {
  background: var(--bg-secondary, #1a2c38);
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.search-wrap {
  display: flex;
}

.search-input {
  border-radius: var(--radius-md, 8px) 0 0 var(--radius-md, 8px);
  width: 220px;
}

.btn-search {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: none;
  border-radius: 0 var(--radius-md, 8px) var(--radius-md, 8px) 0;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  transition: color 150ms ease;
}

.btn-search:hover {
  color: var(--accent-primary, #00e701);
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

tbody tr {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
}

tbody tr:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

.item-name {
  font-weight: 600;
  color: var(--text-primary, #fff);
  font-size: 13px;
}

.item-desc {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 2px;
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.td-mono {
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 13px;
  color: var(--text-primary, #fff);
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

.text-danger {
  color: var(--color-danger, #ed4245);
}

.empty-row {
  text-align: center;
  padding: 48px 20px !important;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  white-space: nowrap;
}

.badge-active {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-inactive {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-type {
  background: rgba(20, 117, 225, 0.15);
  color: #5ba7f0;
}

.badge-type-bonus_credits { background: rgba(0, 231, 1, 0.12); color: #00e701; }
.badge-type-free_spins { background: rgba(20, 117, 225, 0.15); color: #5ba7f0; }
.badge-type-cashback_boost { background: rgba(255, 167, 38, 0.15); color: #ffa726; }
.badge-type-physical { background: rgba(150, 117, 225, 0.15); color: #c39aff; }
.badge-type-tournament_entry { background: rgba(237, 66, 69, 0.12); color: #f08080; }

.badge-vip {
  background: rgba(255, 215, 0, 0.15);
  color: #ffd700;
}

.cat-badge {
  font-size: 12px;
  color: var(--text-secondary, #b1bad3);
}

/* Action buttons */
.action-buttons {
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-tertiary, #7f8fa4);
}

.btn-action:hover {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

.btn-action-danger:hover {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
  border-color: rgba(237, 66, 69, 0.3);
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled):not(.active) {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 20px;
  overflow-y: auto;
}

.modal-panel {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-xl, 16px);
  width: 100%;
  max-width: 600px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}

.modal-panel-sm {
  max-width: 400px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-title {
  font-size: 17px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-tertiary, #7f8fa4);
  transition: all 150ms ease;
}

.modal-close:hover {
  color: var(--text-primary, #fff);
}

.modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 20px;
}

/* Form fields */
.form-section {
  margin-bottom: 24px;
}

.form-section:last-of-type {
  margin-bottom: 0;
}

.section-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 0 16px;
}

.form-row {
  margin-bottom: 14px;
}

.form-row.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.form-input,
.form-select,
.form-textarea {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
  transition: border-color 150ms ease;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.form-select option {
  background: var(--bg-secondary, #1a2c38);
}

.form-textarea {
  resize: vertical;
  min-height: 60px;
}

.form-hint {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  margin-top: 2px;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}

/* Toggle */
.toggle-label {
  flex-direction: row !important;
  align-items: center !important;
  gap: 12px !important;
}

.toggle-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-input {
  display: none;
}

.toggle-track {
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  position: relative;
  transition: background 150ms ease;
}

.toggle-input:checked + .toggle-track {
  background: var(--accent-primary, #00e701);
  border-color: var(--accent-primary, #00e701);
}

.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--text-tertiary, #7f8fa4);
  transition: transform 150ms ease, background 150ms ease;
}

.toggle-input:checked ~ .toggle-track .toggle-thumb {
  transform: translateX(18px);
  background: #000;
}

.toggle-state {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
}

.config-note {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
  font-style: italic;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-xs {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(0, 0, 0, 0.25);
  border-top-color: #000;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.spinner-xs-danger {
  border-color: rgba(237, 66, 69, 0.25);
  border-top-color: var(--color-danger, #ed4245);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .reward-catalog-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .form-row.two-col {
    grid-template-columns: 1fr;
  }

  .filters-row {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }
}
</style>
